import {useEffect, useRef} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useHttpCalls} from '../api/useHttpCalls';
import {selectGridCode, selectIsResizing, selectQueryId} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import type {ActivePreset, FeGridSettings, PresetDetail, VirtualPreset} from '../types/Api';
import type {EagColumnState} from '../types/EagColumnState';
import type {Filters} from '../types/Filters';
import {filterTechnicalArray} from '../utils/filterTechnicalArray';
import {getPresetStates} from '../utils/getPresetStates';
import {isCustomPresetDirty} from '../utils/isCustomPresetDirty';
import {isPresetColumnsSettingsInvalid} from '../utils/isPresetColumnsSettingsInvalid';
import {isPresetCustom} from '../utils/isPresetCustom';
import {isVirtualPresetEqual} from '../utils/isVirtualPresetEqual';
import {getSearchParamsByGridCode} from '../utils/url/getSearchParamsByGridCode';

type UsePresetSyncProps = {
  columnStates: EagColumnState[];
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  activePreset: ActivePreset;
  settings: FeGridSettings;
};

/**
 * Bridges preset state ↔ EagGrid column/filter/sort state.
 *
 * - When activePreset changes → apply columnsSettings + sort from dataQuery atomically
 * - When columnStates/filters/settings change → PUT virtual-preset with new derived state
 *
 * Reads gridCode from DataGridContext and dataQueryId from URL search params internally,
 * eliminating the need to drill updateVirtualPreset through props.
 */
export const usePresetSync = (props: UsePresetSyncProps): void => {
  const http = useHttpCalls();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const isResizing = useEagGridSelector(selectIsResizing);
  const queryId = useEagGridSelector(selectQueryId);

  const lastVirtualPresetState = useRef<VirtualPreset | null>(null);
  // Guard: do not PUT virtual-preset until the active preset has been applied to the grid at least once.
  // This prevents spurious PUT calls during initial store setup (useLayoutEffect fires after first render,
  // causing columnStates to change before the preset is applied).
  const hasPresetBeenApplied = useRef<boolean>(false);

  // Refs for values read inside effects but not meant to trigger re-runs.
  const gridCodeRef = useRef(gridCode);
  const settingsRef = useRef(props.settings);
  const activePresetRef = useRef(props.activePreset);
  const httpRef = useRef(http);
  const storeRef = useRef(store);

  /**
   * Sync latest values to refs for stable-identity effects below.
   * React Compiler requires render purity — ref writes must be in useEffect.
   * Effects fire top-to-bottom within a component, so this sync effect runs
   * BEFORE the consuming effects declared below.
   *
   * @see Vercel advanced-event-handler-refs pattern
   */
  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs refs every render; see JSDoc above
  useEffect(() => {
    gridCodeRef.current = gridCode;
    settingsRef.current = props.settings;
    activePresetRef.current = props.activePreset;
    httpRef.current = http;
    storeRef.current = store;
  });

  // activePreset.columnsSettings reference is stable across unrelated store mutations
  // (e.g. setGridSettings spreads activePreset but copies columnsSettings by reference).
  // Using the reference directly as a dep avoids JSON.stringify serialization every render.
  const columnsSettings = props.activePreset?.columnsSettings;

  // When activePreset's columnsSettings change, apply columns + sort atomically.
  // Uses applyPresetColumnsAndSort (single setState) to avoid double subscriber
  // notification and intermediate state between column and sort updates.
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive preset application; columnsSettings reference changes only when preset content changes, preventing spurious runs on unrelated store mutations
  useEffect(() => {
    const activePreset = activePresetRef.current;

    if (!activePreset) {
      return;
    }

    const cols = activePreset.columnsSettings;

    if (isNilOrEmpty(cols)) {
      return;
    }

    storeRef.current.applyPresetColumnsAndSort(cols);
    hasPresetBeenApplied.current = true;

    // Seed lastVirtualPresetState so the first run of the columnStates/filters/settings
    // effect sees no diff and skips the PUT. Without this, applyPresetColumnsAndSort mutates
    // columnStates in the same React commit batch, Effect 2 fires with
    // lastVirtualPresetState === null → isPresetModified = true → spurious PUT.
    if (!isPresetCustom(activePreset)) {
      const seedQueryId = getSearchParamsByGridCode(gridCodeRef.current)?.queryId ?? '';
      lastVirtualPresetState.current = {
        columnsSettings: filterTechnicalArray(cols),
        gridSettings: settingsRef.current,
        dataQueryId: seedQueryId,
      };
    }
  }, [columnsSettings]);

  // When column states, filters, or settings change → update the virtual preset (only for non-custom presets)
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive virtual-preset sync; fires when user modifies columns or settings; isResizing dep defers PUT until resize session ends (single PUT per drag)
  useEffect(() => {
    // Do not PUT during initial load — wait until the active preset has been applied first.
    // Without this guard: store initialisation (useLayoutEffect) triggers columnStates change
    // before any user interaction, causing a spurious PUT on every mount.
    if (!hasPresetBeenApplied.current) {
      return;
    }

    // Skip PUT while the user is actively resizing a column. The local store already reflects the
    // new width (optimistic UI), so the grid renders correctly. Once the user releases the mouse
    // (isResizing flips to false), this effect re-runs and sends a single PUT with the final width.
    if (isResizing) {
      return;
    }

    if (isPresetCustom(activePresetRef.current)) {
      const savedPreset = activePresetRef.current as PresetDetail;
      const currentColumns = filterTechnicalArray(getPresetStates(props.columnStates));

      const isDirty = isCustomPresetDirty(
        {
          columnsSettings: filterTechnicalArray(savedPreset.columnsSettings),
          gridSettings: savedPreset.gridSettings,
          queryId: savedPreset.dataQueryId,
        },
        {
          columnsSettings: currentColumns,
          gridSettings: props.settings,
          queryId: queryId ?? '',
        }
      );

      storeRef.current.setIsPresetChanged(isDirty);
      return;
    }

    const searchParams = getSearchParamsByGridCode(gridCodeRef.current);
    const dataQueryId = searchParams?.queryId;

    if (!dataQueryId) {
      return;
    }

    const currentColumnSettings = filterTechnicalArray(getPresetStates(props.columnStates));

    const modifiedPreset: VirtualPreset = {
      columnsSettings: currentColumnSettings,
      gridSettings: props.settings,
      dataQueryId: lastVirtualPresetState.current?.dataQueryId ?? dataQueryId,
    };

    if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
      return;
    }

    const isPresetModified = isNilOrEmpty(lastVirtualPresetState.current)
      ? true
      : !isVirtualPresetEqual(lastVirtualPresetState.current!, modifiedPreset);

    if (!isPresetModified) {
      return;
    }

    lastVirtualPresetState.current = modifiedPreset;
    httpRef.current.updateVirtualPreset(modifiedPreset)?.catch(() => {
      // Silent catch — column/settings sync is best-effort.
    });
  }, [props.columnStates, props.settings, isResizing, queryId]);
};
